import { CheckoutItem } from '@ancon/wildcat-types'

import {
  CapacityConflictErrorPayload,
  CapacityConflictLine,
  CapacityConflictModalState,
  CheckoutRequestedItemDetails,
} from '../types'
import isAppDevelopment from '../../app/utils/isAppDevelopment'

import getCapacityConflictAvailableTimes from './getCapacityConflictAvailableTimes'
import getCapacityConflictLines from './getCapacityConflictLines'

/**
 * The join between the error and the cart is the fragile part of this flow: the API
 * names product ids, the client only ever sends variant ids, and a mismatch shows up
 * as a modal with nothing in it. Say so loudly while developing.
 */
function warnOnUnmatchedProducts(
  capacities: Record<string, number>,
  lines: CapacityConflictLine[],
) {
  if (!isAppDevelopment() || !Object.keys(capacities).length) {
    return
  }

  const matched = lines.some(
    line =>
      (line.productId !== null && capacities[line.productId] !== undefined) ||
      capacities[line.variantId] !== undefined,
  )

  if (matched) {
    return
  }

  // eslint-disable-next-line no-console
  console.warn(
    '[capacityConflict] none of the blocked products matched the cart - the modal will be empty.',
    {
      blockedProductIds: Object.keys(capacities),
      lines: lines.map(({ productId, variantId, name }) => ({
        productId,
        variantId,
        name,
      })),
    },
  )
}

/**
 * Turns the refused request and the error it came back with into everything the
 * modal renders from.
 *
 * The cart is resolved here rather than in a selector because it describes what was
 * attempted - the stored checkout still holds the previous state after a rejection.
 */
export default function getCapacityConflictModalState(
  payload: CapacityConflictErrorPayload,
  checkoutItems: CheckoutItem[],
  itemDetails: Record<string, CheckoutRequestedItemDetails>,
): CapacityConflictModalState {
  const { additionalData, requestedItems, requestedTime, origin, outletId } =
    payload
  const { blockingSlotGroups, availablePickupTimeRanges } = additionalData

  // A slot group's limit applies to each of its products, and a product only ever
  // belongs to one group, so the groups flatten away to a plain product map.
  const productCapacities = Object.values(blockingSlotGroups).reduce<
    Record<string, number>
  >(
    (capacities, group) =>
      group.productIds.reduce(
        (withGroup, productId) => ({
          ...withGroup,
          [productId]: group.maximumAllowedQuantity,
        }),
        capacities,
      ),
    {},
  )

  const lines = getCapacityConflictLines(
    requestedItems,
    checkoutItems,
    itemDetails,
  )

  warnOnUnmatchedProducts(productCapacities, lines)

  return {
    availableTimes: getCapacityConflictAvailableTimes(
      availablePickupTimeRanges,
    ),
    productCapacities,
    lines,
    requestedItems,
    requestedTime,
    origin,
    outletId,
    selectedOption: null,
    lineModes: {},
    lineQuantities: {},
  }
}
